import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  FaBars, 
  FaSignOutAlt, 
  FaUser, 
  FaHeart, 
  FaTimes,
  FaCog,
  FaUserCog,
  FaChevronDown,
  FaHome,
  FaTv,
  FaNewspaper
} from 'react-icons/fa';
import { useApp } from '../../contexts/AppContext';
import { useTranslation } from '../../hooks/useTranslation';
import { isSidebarRoute } from './Sidebar';
import LanguageSelector from '../common/LanguageSelector';
import config from '../../config/app.config';

const HeaderWrapper = styled.header`
  background: ${props => props.theme === 'dark' 
    ? 'rgba(15, 23, 42, 0.95)' 
    : 'rgba(0, 0, 0, 0.95)'};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid ${props => props.theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.theme === 'dark' ? '#f8fafc' : '#fff'};
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${props => props.$scrolled ? 'translateY(0)' : 'translateY(0)'};
  box-shadow: ${props => props.$scrolled 
    ? '0 8px 32px rgba(0, 0, 0, 0.3)' 
    : '0 2px 8px rgba(0, 0, 0, 0.1)'};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #e63946, transparent);
    opacity: ${props => props.$scrolled ? 1 : 0};
    transition: opacity 0.3s ease;
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 72px;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    height: 64px;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 1001;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: 12px;
    background: linear-gradient(45deg, transparent, rgba(230, 57, 70, 0.2), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

const LogoImage = styled.img`
  height: 48px;
  width: auto;
  object-fit: contain;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  
  &:hover {
    transform: scale(1.08) rotate(2deg);
  }
  
  @media (max-width: 768px) {
    height: 40px;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: ${props => props.theme === 'dark' ? '#cbd5e1' : '#e2e8f0'};
  text-decoration: none;
  font-weight: 500;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.95rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(230, 57, 70, 0.1), rgba(230, 57, 70, 0.05));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #e63946, #ff6b7a);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform: translateX(-50%);
  }
  
  ${props => props.$isActive && `
    color: #e63946;
    background: rgba(230, 57, 70, 0.1);
    
    &::after {
      width: 80%;
    }
  `}
  
  &:hover {
    color: #e63946;
    transform: translateY(-2px);
    
    &::before {
      opacity: 1;
    }
    
    &::after {
      width: 80%;
    }
  }
`;

const DonationButton = styled(Link)`
  background: linear-gradient(135deg, #e63946 0%, #d52c3d 50%, #c1242f 100%);
  color: white;
  text-decoration: none;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(230, 57, 70, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  &:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 8px 25px rgba(230, 57, 70, 0.4);
  }

  svg {
    font-size: 1rem;
    animation: heartbeat 2s infinite;
  }

  @keyframes heartbeat {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 1001;
`;

const UserDropdown = styled.div`
  position: relative;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: ${props => props.theme === 'dark' 
    ? 'rgba(51, 65, 85, 0.8)' 
    : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(255, 255, 255, 0.2)'};
  color: ${props => props.theme === 'dark' ? '#f8fafc' : '#fff'};
  padding: 0.75rem 1rem;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: ${props => props.theme === 'dark' 
      ? 'rgba(71, 85, 105, 0.9)' 
      : 'rgba(255, 255, 255, 0.15)'};
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    
    span {
      display: none;
    }
  }

  svg:last-child {
    transition: transform 0.3s ease;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: ${props => props.theme === 'dark' 
    ? 'rgba(15, 23, 42, 0.95)' 
    : 'rgba(0, 0, 0, 0.95)'};
  backdrop-filter: blur(20px);
  border: 1px solid ${props => props.theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 16px;
  padding: 0.5rem;
  min-width: 200px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transform: ${props => props.$isOpen 
    ? 'translateY(0) scale(1)' 
    : 'translateY(-10px) scale(0.95)'};
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1002;
`;

const DropdownItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: transparent;
  border: none;
  color: ${props => props.theme === 'dark' ? '#cbd5e1' : '#e2e8f0'};
  text-align: left;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  
  &:hover {
    background: rgba(230, 57, 70, 0.1);
    color: #e63946;
  }
  
  svg {
    color: #e63946;
  }
`;

const MobileMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 999;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  
  @media (min-width: 1025px) {
    display: none;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 320px;
  height: 100vh;
  background: ${props => props.theme === 'dark' 
    ? 'rgba(15, 23, 42, 0.98)' 
    : 'rgba(0, 0, 0, 0.98)'};
  backdrop-filter: blur(20px);
  padding: 2rem;
  transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  overflow-y: auto;
  
  @media (min-width: 1025px) {
    display: none;
  }
  
  @media (max-width: 380px) {
    width: 100vw;
  }
`;

const MobileMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const MobileMenuNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MobileNavLink = styled(Link)`
  color: ${props => props.theme === 'dark' ? '#cbd5e1' : '#e2e8f0'};
  text-decoration: none;
  font-weight: 500;
  padding: 1rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  ${props => props.$isActive && `
    background: rgba(230, 57, 70, 0.1);
    color: #e63946;
  `}
  
  &:hover {
    background: rgba(230, 57, 70, 0.1);
    color: #e63946;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: 1px solid ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)'};
  color: ${props => props.theme === 'dark' ? '#f8fafc' : '#fff'};
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(230, 57, 70, 0.1);
    color: #e63946;
    border-color: #e63946;
  }
  
  @media (max-width: 1024px) {
    display: block;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme === 'dark' ? '#f8fafc' : '#fff'};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(230, 57, 70, 0.1);
    color: #e63946;
  }
`;

const ActionButton = styled.button`
  background: ${props => props.variant === 'primary' ? 
    'linear-gradient(135deg, #e63946, #d52c3d)' : 
    'transparent'};
  color: ${props => props.variant === 'primary' ? 
    'white' : 
    (props.theme === 'dark' ? '#cbd5e1' : '#e2e8f0')};
  border: ${props => props.variant === 'primary' ? 
    'none' : 
    `1px solid ${props.theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)'}`};
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: ${props => props.variant === 'primary' ? 
      'linear-gradient(135deg, #d52c3d, #c1242f)' : 
      'rgba(230, 57, 70, 0.1)'};
    color: ${props => props.variant === 'primary' ? 
      'white' : 
      '#e63946'};
    transform: translateY(-2px);
    box-shadow: ${props => props.variant === 'primary' ? 
      '0 8px 25px rgba(230, 57, 70, 0.3)' : 
      'none'};
  }

  svg {
    font-size: 1rem;
  }
`;

const SidebarMenuButton = styled.button`
  display: flex;
  background: transparent;
  border: 1px solid ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)'};
  color: ${props => props.theme === 'dark' ? '#f8fafc' : '#fff'};
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  margin-right: 1rem;
  
  &:hover {
    background: rgba(230, 57, 70, 0.1);
    color: #e63946;
    border-color: #e63946;
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    margin-right: 0.5rem;
  }
`;

const Header = () => {
  const { auth, theme, toggleSidebar } = useApp();
  const { tAuth, tNav, tCommon } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  const shouldShowSidebarButton = () => {
    return auth.authenticated && isSidebarRoute(location.pathname);
  };

  // Efecto para detectar scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Efecto para cerrar menús al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-dropdown]')) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    if (!auth.logout) {
      console.error('❌ Función logout no está disponible');
      return;
    }

    try {
      console.log('🚪 Iniciando logout...');
      setIsUserDropdownOpen(false);
      
      await auth.logout();
      
      // Fallback por si Keycloak no redirige automáticamente
      console.log('⚠️ Logout completado, redirigiendo...');
      navigate('/login');
      
    } catch (error) {
      console.error('❌ Error en logout:', error);
      
      // Navegar al login como fallback
      setTimeout(() => {
        navigate('/login');
      }, 1000);
    }
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Función para verificar si una ruta está activa
  const isActiveRoute = (path) => {
    if (path === '/' || path === config.routes.public.home) {
      return location.pathname === '/' || location.pathname === config.routes.public.home;
    }
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const navigationItems = auth.authenticated ? [
    { path: '/features', label: tNav('features'), icon: FaCog },
    { path: '/monitor', label: tNav('monitor'), icon: FaTv },
    { path: '/contact', label: tNav('contacts'), icon: FaUser },
    { path: 'https://blog.punchia.online', label: tNav('blog'), icon: FaNewspaper, external: true },
    { path: '/admin', label: tNav('admin'), icon: FaHome }
  ] : [
    { path: '/features', label: tNav('features'), icon: FaCog },
    { path: '/monitor', label: tNav('monitor'), icon: FaTv },
    { path: '/contact', label: tNav('contacts'), icon: FaUser },
    { path: 'https://blog.punchia.online', label: tNav('blog'), icon: FaNewspaper, external: true },
  ];

  return (
    <>
      <HeaderWrapper theme={theme} $scrolled={isScrolled}>
        <HeaderContent>
          <LogoSection>
            {/* Botón de menú sidebar - solo para usuarios autenticados */}
            {shouldShowSidebarButton() && (
              <SidebarMenuButton 
                onClick={toggleSidebar}
                theme={theme}
                title={tCommon('openMenu')}
              >
                <FaBars />
              </SidebarMenuButton>
            )}
            
            <LogoLink to="/">
              <LogoImage src="/Logo.jpg" alt={`${config.app.name} Logo`} />
            </LogoLink>
          </LogoSection>

          {/* Navegación principal */}
          <Nav>
            {navigationItems.map((item) => (
              item.external ? (
                <NavLink 
                  key={item.path}
                  as="a"
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  theme={theme}
                >
                  {item.label}
                </NavLink>
              ) : (
                <NavLink 
                  key={item.path}
                  to={item.path} 
                  theme={theme}
                  $isActive={isActiveRoute(item.path)}
                >
                  {item.label}
                </NavLink>
              )
            ))}
            {/* Botón de donación solo para usuarios no autenticados */}
            {!auth.authenticated && (
              <DonationButton 
                to="/donations"
                $isActive={isActiveRoute('/donations')}
              >
                <FaHeart />
                {tCommon('donate')}
              </DonationButton>
            )}
          </Nav>

          {/* Sección de usuario */}
          <UserSection>
            {/* Selector de idioma */}
            <LanguageSelector theme={theme} compact />

            {auth.authenticated ? (
              <UserDropdown data-dropdown>
                <UserButton 
                  theme={theme}
                  $isOpen={isUserDropdownOpen}
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                >
                  <FaUser />
                  <span>{auth.user?.firstName || tCommon('user')}</span>
                  <FaChevronDown />
                </UserButton>
                <DropdownMenu theme={theme} $isOpen={isUserDropdownOpen}>
                  <DropdownItem theme={theme}>
                    <FaUserCog />
                    {tNav('profile')}
                  </DropdownItem>
                  <DropdownItem theme={theme}>
                    <FaCog />
                    {tNav('settings')}
                  </DropdownItem>
                  <DropdownItem 
                    theme={theme}
                    onClick={() => handleLogout()}
                  >
                    <FaSignOutAlt />
                    {tAuth('logout')}
                  </DropdownItem>
                </DropdownMenu>
              </UserDropdown>
            ) : (
              <ActionButton 
                as={Link}
                to="/login"
                variant="primary"
                theme={theme}
              >
                <FaUser />
                {tAuth('login')}
              </ActionButton>
            )}

            {!shouldShowSidebarButton() && (
              <MobileMenuButton 
                onClick={handleMobileMenuToggle}
                theme={theme}
                title={tCommon('openMobileMenu')}
              >
                <FaBars />
              </MobileMenuButton>
            )}
          </UserSection>
        </HeaderContent>
      </HeaderWrapper>

      {/* Overlay y menú móvil */}
      <MobileMenuOverlay $isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />
      <MobileMenu theme={theme} $isOpen={isMobileMenuOpen}>
        <MobileMenuHeader>
          <LogoImage src="/Logo.jpg" alt={`${config.app.name} Logo`} />
          <CloseButton onClick={closeMobileMenu} theme={theme}>
            <FaTimes />
          </CloseButton>
        </MobileMenuHeader>
        
        <MobileMenuNav>
          {navigationItems.map((item) => (
            item.external ? (
              <MobileNavLink 
                key={item.path}
                as="a"
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                theme={theme}
                onClick={closeMobileMenu}
              >
                <item.icon />
                {item.label}
              </MobileNavLink>
            ) : (
              <MobileNavLink 
                key={item.path}
                to={item.path} 
                theme={theme}
                $isActive={isActiveRoute(item.path)}
                onClick={closeMobileMenu}
              >
                <item.icon />
                {item.label}
              </MobileNavLink>
            )
          ))}
          {/* Donación solo para usuarios no autenticados */}
          {!auth.authenticated && (
            <MobileNavLink 
              to="/donations"
              theme={theme}
              $isActive={isActiveRoute('/donations')}
              onClick={closeMobileMenu}
            >
              <FaHeart />
              {tNav('donations')}
            </MobileNavLink>
          )}
          
          {auth.authenticated && (
            <>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '1rem 0' }} />
              <MobileNavLink 
                as="button" 
                onClick={() => handleLogout()} 
                theme={theme}
              >
                <FaSignOutAlt />
                {tCommon('logout')}
              </MobileNavLink>
            </>
          )}
        </MobileMenuNav>
      </MobileMenu>
    </>
  );
};

export default React.memo(Header); 